import { SearchFilters, SearchResult, SearchResponse, SearchFacets } from './core/types';
import { DisplaySearch } from './displays/DisplaySearch';
import { SequenceSearch } from './sequences/SequenceSearch';
import { SearchCache } from './utils/SearchCache';
import { supabase } from '@/integrations/supabase/client';

/**
 * Advanced Search Service - Main orchestrator for all search operations
 */
export class AdvancedSearchService {
  /**
   * Main search method that combines displays and sequences
   */
  public static async search(filters: SearchFilters): Promise<SearchResponse> {
    const startTime = Date.now();

    try {
      // Check cache first
      const cacheKey = SearchCache.getKey('search', filters);
      const cachedResult = SearchCache.getCachedResult<SearchResponse>(cacheKey);
      
      if (cachedResult) {
        return cachedResult;
      }

      // Execute searches in parallel
      const [displayResults, sequenceResults, facets] = await Promise.all([
        this.searchDisplays(filters),
        this.searchSequences(filters),
        this.generateFacets(filters)
      ]);

      // Combine and sort results
      const allResults = [...displayResults, ...sequenceResults];
      const sortedResults = this.sortResults(allResults, filters);

      // Apply final pagination if not already applied
      const { limit = 20, offset = 0 } = filters;
      const paginatedResults = sortedResults.slice(offset, offset + limit);

      const response: SearchResponse = {
        results: paginatedResults,
        total: allResults.length,
        facets,
        appliedFilters: filters,
        searchTime: Date.now() - startTime,
        suggestions: await this.generateSuggestions(filters, allResults.length)
      };

      // Cache the response
      SearchCache.setCachedResult('search', filters, response, filters);

      return response;
    } catch (error) {
      console.error('Search error:', error);
      throw new Error(`Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Search displays using DisplaySearch module
   */
  private static async searchDisplays(filters: SearchFilters): Promise<SearchResult[]> {
    // Skip display search if we're specifically searching for sequence-only features
    if (this.isSequenceOnlySearch(filters)) {
      return [];
    }

    const displaySearch = new DisplaySearch(filters);
    return await displaySearch.execute();
  }

  /**
   * Search sequences using SequenceSearch module
   */
  private static async searchSequences(filters: SearchFilters): Promise<SearchResult[]> {
    // Skip sequence search if we're specifically searching for display-only features
    if (this.isDisplayOnlySearch(filters)) {
      return [];
    }

    const sequenceSearch = new SequenceSearch(filters);
    return await sequenceSearch.execute();
  }

  /**
   * Check if search is specifically for sequences only
   */
  private static isSequenceOnlySearch(filters: SearchFilters): boolean {
    return !!(
      filters.priceRange ||
      filters.genres ||
      filters.props ||
      filters.softwareTypes ||
      filters.channelCount
    );
  }

  /**
   * Check if search is specifically for displays only
   */
  private static isDisplayOnlySearch(filters: SearchFilters): boolean {
    return !!(
      filters.displayTypes && 
      !filters.priceRange && 
      !filters.genres && 
      !filters.props
    );
  }

  /**
   * Sort combined search results
   */
  private static sortResults(results: SearchResult[], filters: SearchFilters): SearchResult[] {
    const { sortBy = 'relevance', sortOrder = 'desc' } = filters;

    return results.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'distance':
          comparison = (a.distance || Infinity) - (b.distance || Infinity);
          break;
        case 'rating':
          comparison = (b.rating || 0) - (a.rating || 0);
          break;
        case 'price':
          comparison = (a.price || 0) - (b.price || 0);
          break;
        case 'name':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'created_at':
          comparison = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          break;
        case 'relevance':
        default: {
          // Use relevance scores calculated by individual search modules
          const scoreA = a.relevanceScore || 0;
          const scoreB = b.relevanceScore || 0;
          comparison = scoreB - scoreA;
          break;
        }
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }

  /**
   * Generate search facets
   */
  private static async generateFacets(filters: SearchFilters): Promise<SearchFacets> {
    try {
      const [
        holidayTypeFacets,
        displayTypeFacets,
        genreFacets,
        propsFacets
      ] = await Promise.all([
        this.getHolidayTypeFacets(filters),
        this.getDisplayTypeFacets(filters),
        this.getGenreFacets(filters),
        this.getPropsFacets(filters)
      ]);

      return {
        holidayTypes: holidayTypeFacets,
        displayTypes: displayTypeFacets,
        genres: genreFacets,
        softwareTypes: [], // TODO: Implement when schema supports it
        props: propsFacets,
        priceRanges: this.getPriceRangeFacets(),
        ratingRanges: this.getRatingRangeFacets(),
        yearRanges: this.getYearRangeFacets()
      };
    } catch (error) {
      console.error('Error generating facets:', error);
      return this.getEmptyFacets();
    }
  }

  /**
   * Get holiday type facets
   */
  private static async getHolidayTypeFacets(filters: SearchFilters) {
    const { data } = await supabase
      .from('displays')
      .select('holiday_type')
      .not('holiday_type', 'is', null);

    const counts = new Map<string, number>();
    data?.forEach(item => {
      const types = item.holiday_type?.split(',') || [];
      types.forEach(type => {
        counts.set(type.trim(), (counts.get(type.trim()) || 0) + 1);
      });
    });

    return Array.from(counts.entries()).map(([value, count]) => ({
      value,
      label: value,
      count
    }));
  }

  /**
   * Get display type facets
   */
  private static async getDisplayTypeFacets(filters: SearchFilters) {
    const { data } = await supabase
      .from('displays')
      .select('display_type')
      .not('display_type', 'is', null);

    const counts = new Map<string, number>();
    data?.forEach(item => {
      if (item.display_type) {
        counts.set(item.display_type, (counts.get(item.display_type) || 0) + 1);
      }
    });

    return Array.from(counts.entries()).map(([value, count]) => ({
      value,
      label: value,
      count
    }));
  }

  /**
   * Get genre facets
   */
  private static async getGenreFacets(filters: SearchFilters) {
    const { data } = await supabase
      .from('display_songs')
      .select('genre')
      .not('genre', 'is', null)
      .eq('sequence_available', true);

    const counts = new Map<string, number>();
    data?.forEach(item => {
      if (item.genre) {
        counts.set(item.genre, (counts.get(item.genre) || 0) + 1);
      }
    });

    return Array.from(counts.entries()).map(([value, count]) => ({
      value,
      label: value,
      count
    }));
  }

  /**
   * Get props facets
   */
  private static async getPropsFacets(filters: SearchFilters) {
    // This would need to be implemented based on your props schema
    return [];
  }

  /**
   * Get predefined price range facets
   */
  private static getPriceRangeFacets() {
    return [
      { min: 0, max: 0, count: 0, label: 'Free' },
      { min: 0.01, max: 5, count: 0, label: '$0.01 - $5' },
      { min: 5.01, max: 10, count: 0, label: '$5.01 - $10' },
      { min: 10.01, max: 20, count: 0, label: '$10.01 - $20' },
      { min: 20.01, max: 999, count: 0, label: '$20+' }
    ];
  }

  /**
   * Get predefined rating range facets
   */
  private static getRatingRangeFacets() {
    return [
      { min: 4, max: 5, count: 0, label: '4+ Stars' },
      { min: 3, max: 5, count: 0, label: '3+ Stars' },
      { min: 2, max: 5, count: 0, label: '2+ Stars' },
      { min: 1, max: 5, count: 0, label: '1+ Stars' }
    ];
  }

  /**
   * Get predefined year range facets
   */
  private static getYearRangeFacets() {
    const currentYear = new Date().getFullYear();
    return [
      { min: currentYear, max: currentYear, count: 0, label: `${currentYear}` },
      { min: currentYear - 1, max: currentYear - 1, count: 0, label: `${currentYear - 1}` },
      { min: currentYear - 2, max: currentYear - 1, count: 0, label: `${currentYear - 2} - ${currentYear - 1}` },
      { min: 2020, max: currentYear - 3, count: 0, label: `2020 - ${currentYear - 3}` },
      { min: 2010, max: 2019, count: 0, label: '2010 - 2019' }
    ];
  }

  /**
   * Get empty facets as fallback
   */
  private static getEmptyFacets(): SearchFacets {
    return {
      holidayTypes: [],
      displayTypes: [],
      genres: [],
      softwareTypes: [],
      props: [],
      priceRanges: [],
      ratingRanges: [],
      yearRanges: []
    };
  }

  /**
   * Generate search suggestions based on query and results
   */
  private static async generateSuggestions(filters: SearchFilters, resultCount: number): Promise<string[]> {
    if (!filters.query || resultCount > 0) {
      return [];
    }

    // Simple suggestion logic - could be enhanced with ML/AI
    const suggestions: string[] = [];
    const query = filters.query.toLowerCase();

    // Holiday-based suggestions
    const holidays = ['christmas', 'halloween', 'easter', 'thanksgiving'];
    holidays.forEach(holiday => {
      if (holiday.includes(query) || query.includes(holiday)) {
        suggestions.push(`${holiday} lights`);
        suggestions.push(`${holiday} displays`);
      }
    });

    // Generic suggestions
    if (query.length >= 3) {
      suggestions.push(`${query} sequences`);
      suggestions.push(`${query} music`);
    }

    return suggestions.slice(0, 5); // Limit to 5 suggestions
  }

  /**
   * Clear search cache
   */
  public static clearCache(): void {
    SearchCache.clearCache();
  }

  /**
   * Get cache statistics
   */
  public static getCacheStats() {
    return SearchCache.getCacheStats();
  }
}

// Export the main search function for backward compatibility
export const search = AdvancedSearchService.search.bind(AdvancedSearchService);
export const clearSearchCache = AdvancedSearchService.clearCache.bind(AdvancedSearchService);

// Re-export types for convenience
export * from './core/types';
export { GeospatialSearch } from './utils/GeospatialSearch';
export { SearchCache } from './utils/SearchCache';